import React, { useEffect, useContext, useState } from 'react';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { string as stringPropType } from 'prop-types';
import classNames from 'classnames/bind';
import { MOBILE } from '@thd-olt-functional/utils';
import { Row, Col } from '@thd-olt-component-react/grid';
import { ScheduleAMeasure as ScheduleAMeasureButton } from '@thd-olt-component-react/home-services-cta-buttons';
import { Fieldset } from '@thd-olt-component-react/fieldset';
import { Accordion } from '@thd-olt-component-react/accordion';
import { Calculator } from '@thd-olt-component-react/calculator';
import {
  useDataModel, params, number, extend, alias, string,
  shape as shapeType,
} from '@thd-nucleus/data-sources';
import { InfoBoxLink } from '../common/info-box-link/InfoBoxLink';
import { WhatToExpect } from '../common/what-to-expect/WhatToExpect';
import { EstimatedTotal } from './EstimatedTotal';
import { calculateEstimatedTotal, roundUpEstimatedTotal } from '../../utils/utils';

import {
  ICON_URLS,
  samWhatToExpectList,
  FIELDSET_TITLE
} from '../../utils/constants';
import styles from '../home-services.module.scss';

const cx = classNames.bind(styles);

const NonBuyableScheduleAMeasure = ({ itemId }) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('home-services.ready'); }, []);
  const [estimation, setEstimation] = useState(null);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === MOBILE;
  const { storeId } = useStore();
  const opts = {
    variables: {
      itemId,
      storeId
    },
    ssr: false,
    skip: storeId === '8119'
  };
  const { data, loading, error } = useDataModel('clientOnlyProduct', opts);

  if (!itemId || error || loading || !data) {
    return null;
  }

  const {
    value: sqFtPrice,
    unitsPerCase
  } = data?.product?.pricing?.alternate?.unit;

  const onCalculate = ({ coverage }) => {
    const total = calculateEstimatedTotal(coverage, sqFtPrice);
    const estimatedTotal = roundUpEstimatedTotal(total);
    setEstimation({ estimatedTotal, coverage });
  };

  return (
    <div
      className={cx('home-services')}
      data-testid="home-services-sam"
    >
      <Fieldset
        title={FIELDSET_TITLE}
        iconUrl={ICON_URLS.tools}
        border={!isMobile}
      >
        <Row>
          <Col xs="12">
            <section className="lead-gen__title">
              <strong className="u__text--success lead-gen__title--highlight">FREE </strong>
              carpet samples when you schedule an in-home measure
            </section>

            <WhatToExpect whatToExpectList={samWhatToExpectList} />
            <ScheduleAMeasureButton itemId={itemId} />
            <hr />
            <InfoBoxLink
              title="Service Reviews"
              href="https://www.homedepot.com/services/c/carpet-installation/b76e9e302/reviews/page/1"
            />
          </Col>
        </Row>
      </Fieldset>

      <Accordion type="checkbox" name="calculator">
        <Accordion.Item
          title="How Much Flooring Will You Need?"
        >
          <Calculator
            onCalculate={onCalculate}
            lineItemName="Area"
            dimensionNames={['Length', 'Width']}
            maxAreaNumber={3}
            hideHeader
            hideMeasuringTips
            unitsPerCase={unitsPerCase}
          />
          <EstimatedTotal
            total={estimation?.estimatedTotal}
            isMobile={isMobile}
            coverage={estimation?.coverage}
          />

        </Accordion.Item>
      </Accordion>
    </div>
  );
};

NonBuyableScheduleAMeasure.displayName = 'NonBuyableScheduleAMeasure';
NonBuyableScheduleAMeasure.propTypes = {
  itemId: stringPropType.isRequired,
};

NonBuyableScheduleAMeasure.dataModel = extend({
  clientOnlyProduct: alias('product').params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    pricing: params({ storeId: string() }).shape({
      alternate: shapeType({
        unit: {
          caseUnitOfMeasure: string(),
          unitsOriginalPrice: number({ float: true }),
          unitsPerCase: number({ float: true }),
          value: number({ float: true }),
        },
      }),
    }),
  })
},
ScheduleAMeasureButton
);

export { NonBuyableScheduleAMeasure };
